import React, { useState, useEffect } from 'react';
import { supabase } from './supabase';

const ETAPAS = [
  { id: 'Cotización', color: '#F59E0B' },
  { id: 'Cita', color: '#3B82F6' },
  { id: 'Seguimiento', color: '#8B5CF6' },
  { id: 'Expediente', color: '#EC4899' },
  { id: 'Cierre ganado', color: '#10B981' },
  { id: 'Cierre perdido', color: '#EF4444' },
];

const COLORES_FUENTE = ['#3B82F6', '#8B5CF6', '#F59E0B', '#EC4899', '#10B981', '#F97316'];

function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);
  React.useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

function pad(n) { return n.toString().padStart(2, '0'); }
function fechaISO(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }
function haceDias(n) { const d = new Date(); d.setDate(d.getDate() - n); return d; }
const fmt = (n) => `$${Number(n || 0).toLocaleString('es-MX', { maximumFractionDigits: 0 })}`;

function BarraHorizontal({ label, valor, max, color, sufijo = '' }) {
  const pct = max > 0 ? Math.max((valor / max) * 100, valor > 0 ? 3 : 0) : 0;
  return (
    <div style={{ marginBottom: '10px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' }}>
        <span style={{ color: '#555' }}>{label}</span>
        <span style={{ fontWeight: '600', color: '#1a1a2e' }}>{valor}{sufijo}</span>
      </div>
      <div style={{ height: '8px', background: '#f0f0f0', borderRadius: '4px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: '4px', transition: 'width 0.3s' }} />
      </div>
    </div>
  );
}

function KpiCard({ label, valor, color, isMobile }) {
  return (
    <div style={{ background: '#fff', border: '0.5px solid #e0e0e0', borderRadius: '12px', padding: isMobile ? '14px' : '18px', flex: 1, minWidth: isMobile ? '45%' : '150px' }}>
      <div style={{ fontSize: '11px', color: '#888', marginBottom: '6px' }}>{label}</div>
      <div style={{ fontSize: isMobile ? '22px' : '26px', fontWeight: '700', color: color || '#1a1a2e' }}>{valor}</div>
    </div>
  );
}

export default function ReportesMarketing({ miRol, miAgente }) {
  const isMobile = useIsMobile();
  const [desarrollos, setDesarrollos] = useState([]);
  const [filtroDesarrollo, setFiltroDesarrollo] = useState('');
  const [fechaInicio, setFechaInicio] = useState(fechaISO(haceDias(30)));
  const [fechaFin, setFechaFin] = useState(fechaISO(new Date()));
  const [contactos, setContactos] = useState([]);
  const [negocios, setNegocios] = useState([]);
  const [leadsPendientes, setLeadsPendientes] = useState(0);
  const [loading, setLoading] = useState(true);

  const esAdminGeneral = miRol === 'Super Admin' || miRol === 'Admin' || miRol === 'Sub Admin';

  useEffect(() => { cargarDesarrollos(); }, []);
  useEffect(() => { cargarDatos(); }, [desarrollos, filtroDesarrollo, fechaInicio, fechaFin]);

  const desarrollosPermitidos = () => {
    if (esAdminGeneral) return desarrollos.map(d => d.nombre);
    return miAgente?.desarrollos_cargo || [];
  };

  const cargarDesarrollos = async () => {
    let query = supabase.from('desarrollos').select('id, nombre').order('nombre');
    if (!esAdminGeneral) {
      const asignados = miAgente?.desarrollos_cargo || [];
      if (asignados.length === 0) { setDesarrollos([]); return; }
      query = query.in('nombre', asignados);
    }
    const { data } = await query;
    setDesarrollos(data || []);
  };

  const cargarDatos = async () => {
    setLoading(true);
    const permitidos = desarrollosPermitidos();
    if (!esAdminGeneral && permitidos.length === 0) {
      setContactos([]); setNegocios([]); setLeadsPendientes(0); setLoading(false); return;
    }

    const finConHora = `${fechaFin}T23:59:59`;
    const inicioConHora = `${fechaInicio}T00:00:00`;

    // Contactos (proxy de leads capturados/convertidos)
    let qContactos = supabase.from('contactos').select('fuente_medio, desarrollo, created_at, asesor_ventas')
      .gte('created_at', inicioConHora).lte('created_at', finConHora);
    if (filtroDesarrollo) qContactos = qContactos.eq('desarrollo', filtroDesarrollo);
    else if (!esAdminGeneral) qContactos = qContactos.in('desarrollo', permitidos);

    // Negocios (embudo)
    let qNegocios = supabase.from('negocios').select('etapa, desarrollo, asesor_ventas, valor, created_at')
      .gte('created_at', inicioConHora).lte('created_at', finConHora);
    if (filtroDesarrollo) qNegocios = qNegocios.eq('desarrollo', filtroDesarrollo);
    else if (!esAdminGeneral) qNegocios = qNegocios.in('desarrollo', permitidos);

    // Leads pendientes de asignar (snapshot actual, no filtrado por fecha)
    const idsDesarrollo = desarrollos.map(d => d.id);
    let qLeads = supabase.from('leads').select('id', { count: 'exact', head: true });
    if (filtroDesarrollo) {
      const dSel = desarrollos.find(d => d.nombre === filtroDesarrollo);
      if (dSel) qLeads = qLeads.eq('desarrollo_id', dSel.id);
    } else if (!esAdminGeneral) {
      if (idsDesarrollo.length > 0) qLeads = qLeads.in('desarrollo_id', idsDesarrollo);
    }

    const [{ data: cData }, { data: nData }, { count: lCount }] = await Promise.all([qContactos, qNegocios, qLeads]);
    setContactos(cData || []);
    setNegocios(nData || []);
    setLeadsPendientes(lCount || 0);
    setLoading(false);
  };

  // --- Cálculos ---
  const totalConvertidos = contactos.length;
  const totalNegocios = negocios.length;
  const negociosGanados = negocios.filter(n => n.etapa === 'Cierre ganado').length;
  const negociosPerdidos = negocios.filter(n => n.etapa === 'Cierre perdido').length;
  const tasaConversion = totalNegocios > 0 ? ((negociosGanados / totalNegocios) * 100).toFixed(1) : '0.0';
  const valorGanado = negocios.filter(n => n.etapa === 'Cierre ganado').reduce((s, n) => s + Number(n.valor || 0), 0);

  const fuentes = {};
  contactos.forEach(c => { const f = c.fuente_medio || 'Sin especificar'; fuentes[f] = (fuentes[f] || 0) + 1; });
  const fuentesOrdenadas = Object.entries(fuentes).sort((a, b) => b[1] - a[1]);
  const maxFuente = Math.max(1, ...fuentesOrdenadas.map(([, v]) => v));

  const etapaConteo = ETAPAS.map(e => ({ ...e, total: negocios.filter(n => n.etapa === e.id).length }));
  const maxEtapa = Math.max(1, ...etapaConteo.map(e => e.total));

  const porDesarrollo = {};
  contactos.forEach(c => { const d = c.desarrollo || 'Sin desarrollo'; porDesarrollo[d] = (porDesarrollo[d] || 0) + 1; });
  const desarrolloOrdenados = Object.entries(porDesarrollo).sort((a, b) => b[1] - a[1]);
  const maxDesarrollo = Math.max(1, ...desarrolloOrdenados.map(([, v]) => v));

  const asesores = {};
  negocios.forEach(n => {
    const a = n.asesor_ventas || 'Sin asignar';
    if (!asesores[a]) asesores[a] = { total: 0, ganados: 0, valor: 0 };
    asesores[a].total++;
    if (n.etapa === 'Cierre ganado') { asesores[a].ganados++; asesores[a].valor += Number(n.valor || 0); }
  });
  const asesoresOrdenados = Object.entries(asesores).sort((a, b) => b[1].ganados - a[1].ganados || b[1].total - a[1].total);

  return (
    <div style={{ padding: isMobile ? '1rem' : '2rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '10px' }}>
        <div>
          <h2 style={{ fontSize: isMobile ? '17px' : '20px', fontWeight: '500', color: '#1a1a2e', marginBottom: '4px' }}>Reportes de Marketing</h2>
          <div style={{ fontSize: '13px', color: '#888' }}>Resultados de leads y negocios</div>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          <input type='date' value={fechaInicio} onChange={e => setFechaInicio(e.target.value)}
            style={{ padding: '7px 10px', border: '0.5px solid #ddd', borderRadius: '6px', fontSize: '13px' }} />
          <span style={{ color: '#aaa', fontSize: '13px' }}>a</span>
          <input type='date' value={fechaFin} onChange={e => setFechaFin(e.target.value)}
            style={{ padding: '7px 10px', border: '0.5px solid #ddd', borderRadius: '6px', fontSize: '13px' }} />
          <select value={filtroDesarrollo} onChange={e => setFiltroDesarrollo(e.target.value)}
            style={{ padding: '7px 10px', border: '0.5px solid #ddd', borderRadius: '6px', fontSize: '13px', background: '#fff' }}>
            <option value=''>Todos los desarrollos</option>
            {desarrollos.map(d => <option key={d.id} value={d.nombre}>{d.nombre}</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', color: '#888', padding: '3rem' }}>Cargando...</div>
      ) : (
        <>
          {/* KPIs */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
            <KpiCard label="Leads capturados" valor={totalConvertidos} isMobile={isMobile} />
            <KpiCard label="Leads sin asignar (hoy)" valor={leadsPendientes} color="#F59E0B" isMobile={isMobile} />
            <KpiCard label="Negocios en el periodo" valor={totalNegocios} isMobile={isMobile} />
            <KpiCard label="Cierres ganados" valor={negociosGanados} color="#10B981" isMobile={isMobile} />
            <KpiCard label="Tasa de conversión" valor={`${tasaConversion}%`} color="#3B82F6" isMobile={isMobile} />
            <KpiCard label="Valor cerrado" valor={fmt(valorGanado)} color="#10B981" isMobile={isMobile} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            {/* Leads por fuente */}
            <div style={{ background: '#fff', border: '0.5px solid #e0e0e0', borderRadius: '12px', padding: '18px' }}>
              <div style={{ fontSize: '13px', fontWeight: '600', color: '#1a1a2e', marginBottom: '14px' }}>Leads por fuente</div>
              {fuentesOrdenadas.length === 0 ? (
                <div style={{ fontSize: '13px', color: '#aaa', textAlign: 'center', padding: '1rem' }}>Sin datos en este periodo</div>
              ) : fuentesOrdenadas.map(([fuente, valor], i) => (
                <BarraHorizontal key={fuente} label={fuente} valor={valor} max={maxFuente} color={COLORES_FUENTE[i % COLORES_FUENTE.length]} />
              ))}
            </div>

            {/* Embudo de negocios */}
            <div style={{ background: '#fff', border: '0.5px solid #e0e0e0', borderRadius: '12px', padding: '18px' }}>
              <div style={{ fontSize: '13px', fontWeight: '600', color: '#1a1a2e', marginBottom: '14px' }}>Negocios por etapa</div>
              {totalNegocios === 0 ? (
                <div style={{ fontSize: '13px', color: '#aaa', textAlign: 'center', padding: '1rem' }}>Sin datos en este periodo</div>
              ) : etapaConteo.map(e => (
                <BarraHorizontal key={e.id} label={e.id} valor={e.total} max={maxEtapa} color={e.color} />
              ))}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
            {/* Leads por desarrollo */}
            {!filtroDesarrollo && (
              <div style={{ background: '#fff', border: '0.5px solid #e0e0e0', borderRadius: '12px', padding: '18px' }}>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#1a1a2e', marginBottom: '14px' }}>Leads por desarrollo</div>
                {desarrolloOrdenados.length === 0 ? (
                  <div style={{ fontSize: '13px', color: '#aaa', textAlign: 'center', padding: '1rem' }}>Sin datos en este periodo</div>
                ) : desarrolloOrdenados.map(([nombre, valor], i) => (
                  <BarraHorizontal key={nombre} label={nombre} valor={valor} max={maxDesarrollo} color={COLORES_FUENTE[i % COLORES_FUENTE.length]} />
                ))}
              </div>
            )}

            {/* Ranking de asesores */}
            <div style={{ background: '#fff', border: '0.5px solid #e0e0e0', borderRadius: '12px', overflow: 'hidden', gridColumn: filtroDesarrollo ? '1 / -1' : 'auto' }}>
              <div style={{ padding: '18px 18px 0', fontSize: '13px', fontWeight: '600', color: '#1a1a2e' }}>Ranking de asesores</div>
              {asesoresOrdenados.length === 0 ? (
                <div style={{ fontSize: '13px', color: '#aaa', textAlign: 'center', padding: '2rem' }}>Sin negocios en este periodo</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', marginTop: '12px' }}>
                  <thead>
                    <tr style={{ borderBottom: '0.5px solid #f0f0f0' }}>
                      <th style={{ padding: '8px 18px', textAlign: 'left', fontWeight: '500', color: '#888', fontSize: '11px' }}>Asesor</th>
                      <th style={{ padding: '8px 18px', textAlign: 'center', fontWeight: '500', color: '#888', fontSize: '11px' }}>Negocios</th>
                      <th style={{ padding: '8px 18px', textAlign: 'center', fontWeight: '500', color: '#888', fontSize: '11px' }}>Ganados</th>
                      <th style={{ padding: '8px 18px', textAlign: 'right', fontWeight: '500', color: '#888', fontSize: '11px' }}>Valor cerrado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {asesoresOrdenados.map(([nombre, datos]) => (
                      <tr key={nombre} style={{ borderBottom: '0.5px solid #f5f5f5' }}>
                        <td style={{ padding: '10px 18px', fontWeight: '500', color: '#1a1a2e' }}>{nombre}</td>
                        <td style={{ padding: '10px 18px', textAlign: 'center', color: '#333' }}>{datos.total}</td>
                        <td style={{ padding: '10px 18px', textAlign: 'center', color: '#10B981', fontWeight: '600' }}>{datos.ganados}</td>
                        <td style={{ padding: '10px 18px', textAlign: 'right', color: '#333' }}>{fmt(datos.valor)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}